import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import AddressMapModal, { buildEmbedUrl, useClientCoordinates } from './addressMapModal'

/**
 * Address row + compact inline map strip for the client card.
 *
 * The client asked for the location to be "immediately accessible as part of
 * the operational view" rather than hidden behind a click, so the map is
 * embedded straight into the card. The address stays exactly where it was, in
 * plain text above the strip — the map supplements it, it never replaces it.
 *
 * The strip is deliberately short and the iframe is pointer-events:none, so it
 * can never swallow a scroll gesture (the embed zooms on wheel) or steal focus
 * while someone is working through the job form. A transparent overlay button
 * sits on top and opens the existing full-size modal for a proper look.
 *
 * Coordinates are resolved once per client by useClientCoordinates — a card
 * re-rendering never re-fires the geocode request.
 */
const InlineAddressMap = ({ address, clientId, latitude, longitude, cancelled = false }) => {
    const { t } = useTranslation()
    const [mapModal, setMapModal] = useState(false)
    // Gated on the address: a client with nothing to geocode should never cost
    // a request. (The hook has to be called unconditionally, hence the flag
    // rather than an early return above it.)
    const { coords, loading, failed, ready } = useClientCoordinates(clientId, latitude, longitude, !!address)

    if (!address) return null

    const openMap = () => setMapModal(true)

    return (
        <>
            <button type="button" className="jwc-addr jwc-addr-btn" title={t('addressMap.openLargerMap', 'Open larger map')} onClick={openMap}>
                <span className="jwc-map-btn">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                </span>
                <span className="jwc-addr-text">{address}</span>
                <span className="jwc-addr-hint">{t('addressMap.enlarge', 'Enlarge')}<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg></span>
            </button>
            <div className={`jwc-mapstrip${cancelled ? ' jwc-mapstrip-cancelled' : ''}`}>
                {ready
                    ? <>
                        <iframe
                            title={t('addressMap.clientLocation', 'Client location')}
                            className="jwc-mapstrip-frame"
                            src={buildEmbedUrl(coords.latitude, coords.longitude)}
                            loading="lazy"
                        />
                        <button type="button" className="jwc-mapstrip-open" onClick={openMap} aria-label={t('addressMap.openLargerMap', 'Open larger map')}>
                            <span className="jwc-mapstrip-cta">
                                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                {t('addressMap.largerMap', 'Larger map')}
                            </span>
                        </button>
                    </>
                    : <span className="jwc-mapstrip-msg">
                        {loading ? t('addressMap.locating', 'Locating address...')
                            : failed ? t('addressMap.notFoundMap', "Couldn't find this address on the map.")
                                : t('addressMap.noMapLocation', 'No map location yet for this address.')}
                    </span>}
            </div>
            {/* Honesty note kept from the modal — a pin dropped on the nearest
                matched area must never read as the exact door. */}
            {ready && coords.approximate &&
                <div className="jwc-mapstrip-note">{t('addressMap.nearestMatch', 'Nearest matched area.')}</div>}
            {/* The resolved coordinates are handed to the modal so opening it
                never triggers a second geocode round-trip. */}
            <AddressMapModal display={mapModal} toggle={setMapModal} address={address} clientId={clientId}
                latitude={coords.latitude} longitude={coords.longitude} />
        </>
    )
}

export default InlineAddressMap
